import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Carousel.scss';
import coinIcon from '../assets/images/coin.webp';
import taxIcon from '../assets/images/tax.webp';
import disposeIcon from '../assets/images/dispose.webp';
import shieldIcon from '../assets/images/shield.webp';

const Carousel = (props) => {
  const mobileBreakpoint = 768;

  const iconMap = {
    coinIcon,
    taxIcon,
    disposeIcon,
    shieldIcon,
  };

  const slides = useMemo(() => {
    const cards = props?.fields?.carouselCards || [];

    return cards.map((card, index) => {
      const fields = card.fields || {};

      return {
        id: card.id || `slide-${index}`,
        image: fields.cardImage?.value?.src || '',
        mobileImage: fields.mobileImage?.value?.src || '',
        theme: fields.theme?.value || '',
        titleLines: fields.titleLines?.map((item) => item?.fields?.Name?.value) || [],
        description: fields.description?.value || '',
        features:
          fields.features?.map((feature) => ({
            id: feature.fields?.id?.value || '',
            icon: iconMap[feature.fields?.icon?.value] || '',
            lines: feature.fields?.lines?.map((line) => line.fields?.Name?.value) || [],
          })) || [],
        buttonText: fields.buttonText?.value || '',
      };
    });
  }, [props?.fields?.carouselCards]);

  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobileHero, setIsMobileHero] = useState(false);

  useEffect(() => {
    const checkScreen = () => {
      setIsMobileHero(window.innerWidth <= mobileBreakpoint);
    };

    checkScreen(); // run once on load

    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  const currentSlide = slides.length
    ? slides[activeSlide]
    : {
        id: '',
        titleLines: [],
        description: '',
        features: [],
        buttonText: '',
        theme: '',
        image: '',
        mobileImage: '',
      };

  useEffect(() => {
    if (!slides.length) return;

    const firstSlide = slides[0];

    const imageUrl =
      isMobileHero && firstSlide.mobileImage ? firstSlide.mobileImage : firstSlide.image;

    if (!imageUrl) return;

    const existingPreload = document.querySelector(`link[rel="preload"][href="${imageUrl}"]`);

    if (existingPreload) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageUrl;
    link.setAttribute('fetchpriority', 'high');

    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, [slides, isMobileHero]);

  const pushToDataLayer = useCallback((eventData) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ...eventData,
      BrandName: 'Corp Web',
      VariantName: 'NA',
    });
  }, []);

  const handleEnquireClick = useCallback(() => {
    if (!currentSlide) return;

    const bannerTitle = currentSlide.titleLines.join(' ') || 'Banner';

    pushToDataLayer({
      event: 'Banner_Interaction',
      eventCategory: 'Banner Interaction',
      eventAction: currentSlide.buttonText || 'Enquire Now',
      eventLabel: 'NA',
      BannerName: bannerTitle,
    });

    const formSection = document.getElementById('returns-form');

    if (formSection) {
      formSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [currentSlide, pushToDataLayer]);

  const handleDotClick = useCallback(
    (index) => {
      const bannerTitle = slides[index]?.titleLines.join(' ') || `Banner ${index + 1}`;

      pushToDataLayer({
        event: 'Banner_Navigation',
        eventCategory: 'Banner Navigation',
        eventAction: 'Dot',
        eventLabel: bannerTitle,
        BannerName: bannerTitle,
      });

      setActiveSlide(index);
    },
    [slides, pushToDataLayer]
  );

  useEffect(() => {
    if (!slides.length) return;

    const intervalId = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(intervalId);
  }, [slides]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileHero(window.innerWidth <= mobileBreakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* -------------------------------------------
     RENDER
  -------------------------------------------- */

  return (
    <div
      className={`hero-slide ${currentSlide.theme === 'light' ? 'hero-slide--light' : ''}`}
      style={{
        backgroundImage: `url(${
          isMobileHero && currentSlide.mobileImage ? currentSlide.mobileImage : currentSlide.image
        })`,
      }}
    >
      <div className="hero-overlay" />

      <div className="bannerCol">
        <div className="imgWrapBanner">
          <img src={props?.fields?.ScrapImage?.value?.src} alt="Hero" />
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            {currentSlide.titleLines.map((line, index) => (
              <span key={`${currentSlide.id}-title-${index}`}>
                {line}
                {index < currentSlide.titleLines.length}
              </span>
            ))}
          </h1>

          <p className="hero-description">{currentSlide.description}</p>

          {currentSlide.features && (
            <div className="hero-features">
              {currentSlide.features.map((feature) => (
                <div key={`${currentSlide.id}-${feature.id}`} className="hero-feature">
                  {feature.icon && (
                    <img
                      src={feature.icon}
                      alt={`${feature.id} icon`}
                      className="hero-feature-icon"
                      loading="lazy"
                    />
                  )}

                  <span className="hero-feature-text">
                    {feature.lines.map((line, index) => (
                      <span key={`${feature.id}-line-${index}`}>
                        {line}
                        {index < feature.lines.length - 1 && <br />}
                      </span>
                    ))}
                  </span>
                </div>
              ))}
              <button className="hero-button" type="button" onClick={handleEnquireClick}>
                {currentSlide.buttonText}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="text-under-banner">
        <div className="side2Txt">
          <p className="mr-19">
            <img src={props?.fields?.Rupees?.value?.src} alt="Terms 1" />
            {props?.fields?.terms1?.value}
          </p>
          <p>
            <img src={props?.fields?.Benefits?.value?.src} alt="Terms 2" />
            {props?.fields?.terms2?.value}
          </p>
        </div>
        <p>
          <img src={props?.fields?.Norms?.value?.src} alt="Terms 3" />
          <span>{props?.fields?.terms3?.value}</span>
        </p>
      </div>
      <div className="hero-dots">
        {slides.map((slide, index) => (
          <span
            key={slide.id}
            className={`hero-dot ${index === activeSlide ? 'active' : ''}`}
            onClick={() => handleDotClick(index)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
